/**
 * Gift With Purchase — pure helper functions.
 * Exposed on window.GWP for reuse and console testing.
 */
(function () {
  'use strict';

  /** @returns {object | null} */
  function getConfig() {
    return window.BBThemeSettings?.gwp ?? null;
  }

  /**
   * Line item properties may be keyed with or without leading underscores in cart.js.
   *
   * @param {Record<string, string>} [properties]
   * @param {string} key
   * @returns {string | undefined}
   */
  function getLineProperty(properties, key) {
    if (!properties || typeof properties !== 'object') return undefined;
    if (properties[key] != null && properties[key] !== '') return String(properties[key]);
    return undefined;
  }

  /**
   * @param {{ properties?: Record<string, string> }} item
   * @returns {boolean}
   */
  function isGwpGiftItem(item) {
    return getLineProperty(item.properties, '_gwp') === 'true';
  }

  /** @type {Map<string, boolean>} */
  const productTagCache = new Map();

  /** @type {Map<string, Promise<void>>} */
  const productTagFetchInFlight = new Map();

  /**
   * @param {object | null} [config]
   * @returns {string}
   */
  function getSaleTag(config) {
    config = config || getConfig();
    const tag = config?.saleTag || 'sale';
    return String(tag).trim().toLowerCase();
  }

  /**
   * @param {string[] | string | undefined} tags
   * @returns {string[]}
   */
  function normalizeProductTags(tags) {
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string' && tags.trim()) {
      return tags.split(',').map(function (tag) {
        return tag.trim();
      });
    }
    return [];
  }

  /**
   * @param {string[] | string | undefined} tags
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function tagsContainSaleTag(tags, config) {
    const saleTag = getSaleTag(config);
    if (!saleTag) return false;

    return normalizeProductTags(tags).some(function (tag) {
      return String(tag).trim().toLowerCase() === saleTag;
    });
  }

  /**
   * @param {object} item
   * @returns {boolean}
   */
  function hasSaleTagProperty(item) {
    return getLineProperty(item.properties, '_sale_tag') === 'true';
  }

  /**
   * @param {object} item
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function isSaleItem(item, config) {
    if (isGwpGiftItem(item)) return false;
    if (hasSaleTagProperty(item)) return true;

    const handle = item.handle;
    if (!handle) return false;

    return productTagCache.get(handle) === true;
  }

  /**
   * @param {string} handle
   * @param {object | null} [config]
   * @returns {Promise<void>}
   */
  function fetchProductTagStatus(handle, config) {
    if (productTagCache.has(handle)) {
      return Promise.resolve();
    }

    const inFlight = productTagFetchInFlight.get(handle);
    if (inFlight) return inFlight;

    const request = fetch('/products/' + handle + '.js')
      .then(function (response) {
        if (!response.ok) {
          productTagCache.set(handle, false);
          return;
        }

        return response.json().then(function (product) {
          productTagCache.set(handle, tagsContainSaleTag(product.tags, config));
        });
      })
      .catch(function () {
        productTagCache.set(handle, false);
      })
      .finally(function () {
        productTagFetchInFlight.delete(handle);
      });

    productTagFetchInFlight.set(handle, request);
    return request;
  }

  /**
   * Resolve sale tag status for cart lines missing the _sale_tag property.
   *
   * @param {{ items?: object[] }} cart
   * @param {object | null} [config]
   * @returns {Promise<void>}
   */
  function ensureProductTagData(cart, config) {
    config = config || getConfig();
    if (!cart?.items?.length) return Promise.resolve();

    const handles = new Set();

    cart.items.forEach(function (item) {
      if (isGwpGiftItem(item)) return;
      if (hasSaleTagProperty(item)) return;

      const handle = item.handle;
      if (!handle || productTagCache.has(handle)) return;

      handles.add(handle);
    });

    if (!handles.size) return Promise.resolve();

    return Promise.all(
      Array.from(handles).map(function (handle) {
        return fetchProductTagStatus(handle, config);
      }),
    ).then(function () {});
  }

  /**
   * @param {{ items?: object[] }} cart
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function hasSaleItemInCart(cart, config) {
    if (!cart?.items?.length) return false;

    return cart.items.some(function (item) {
      return !isGwpGiftItem(item) && isSaleItem(item, config);
    });
  }

  /**
   * Sum original_line_price for full-priced, non-gift cart lines.
   *
   * @param {{ items?: object[] }} cart
   * @param {object | null} [config]
   * @returns {number}
   */
  function getEligibleSubtotal(cart, config) {
    if (!cart?.items?.length) return 0;

    return cart.items.reduce(function (sum, item) {
      if (isGwpGiftItem(item)) return sum;
      if (isSaleItem(item, config)) return sum;
      return sum + (item.original_line_price || 0);
    }, 0);
  }

  /**
   * @param {{ items?: object[] }} cart
   * @returns {boolean}
   */
  function hasGiftInCart(cart) {
    if (!cart?.items?.length) return false;
    return cart.items.some(isGwpGiftItem);
  }

  /**
   * @param {{ items?: object[] }} cart
   * @returns {object | null}
   */
  function getGiftLineItem(cart) {
    if (!cart?.items?.length) return null;
    return cart.items.find(isGwpGiftItem) || null;
  }

  /**
   * @param {{ items?: object[] }} cart
   * @returns {object[]}
   */
  function getGiftLineItems(cart) {
    if (!cart?.items?.length) return [];
    return cart.items.filter(isGwpGiftItem);
  }

  /**
   * Total quantity of all GWP gift lines in the cart.
   *
   * @param {{ items?: object[] }} cart
   * @returns {number}
   */
  function getGiftQuantity(cart) {
    if (!cart?.items?.length) return 0;

    return cart.items.reduce(function (total, item) {
      if (!isGwpGiftItem(item)) return total;
      return total + (item.quantity || 0);
    }, 0);
  }

  /**
   * @param {number | string} variantId
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function isValidGiftVariant(variantId, config) {
    config = config || getConfig();
    if (!config?.allowedVariantIds?.length) return false;

    const id = Number(variantId);
    if (!Number.isFinite(id)) return false;

    return config.allowedVariantIds.some(function (allowedId) {
      return Number(allowedId) === id;
    });
  }

  /**
   * @param {number | string} variantId
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function isGiftVariantAvailable(variantId, config) {
    config = config || getConfig();
    if (!config?.gifts?.length) return false;

    const id = Number(variantId);
    if (!Number.isFinite(id)) return false;

    for (let i = 0; i < config.gifts.length; i++) {
      const gift = config.gifts[i];
      const variants = gift.variants || [];

      for (let j = 0; j < variants.length; j++) {
        if (Number(variants[j].id) === id) {
          return Boolean(variants[j].available);
        }
      }
    }

    return false;
  }

  /**
   * @returns {number}
   */
  function getCurrencyRate() {
    var rate = parseFloat(window.Shopify?.currency?.rate || 1);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  /**
   * Threshold in the customer's presentment currency (minor units).
   *
   * @param {object | null} [config]
   * @returns {number}
   */
  function getThresholdCents(config) {
    config = config || getConfig();
    var baseCents = config?.thresholdCents || 0;
    var shopCurrency = config?.shopCurrency;
    var activeCurrency = window.Shopify?.currency?.active;

    if (!shopCurrency || !activeCurrency || shopCurrency === activeCurrency) {
      return baseCents;
    }

    return Math.round(baseCents * getCurrencyRate());
  }

  /**
   * @param {{ items?: object[] }} cart
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function isEligibleForGift(cart, config) {
    config = config || getConfig();
    if (!config?.enabled) return false;
    if (!config?.gifts?.length) return false;

    return getEligibleSubtotal(cart, config) >= getThresholdCents(config);
  }

  var GWP_IN_CART_EXCLUSION_MIN_QTY = 3;

  /**
   * Total non-gift quantity for a product in the cart.
   *
   * @param {{ items?: object[] }} cart
   * @param {number | string} productId
   * @returns {number}
   */
  function getNonGiftProductQuantity(cart, productId) {
    if (!cart?.items?.length) return 0;

    return cart.items.reduce(function (total, item) {
      if (Number(item.product_id) !== Number(productId)) return total;
      if (isGwpGiftItem(item)) return total;
      return total + (item.quantity || 0);
    }, 0);
  }

  /**
   * @param {{ items?: object[] }} cart
   * @param {number | string} productId
   * @returns {boolean}
   */
  function isProductInCartAsNonGift(cart, productId) {
    return (
      getNonGiftProductQuantity(cart, productId) >= GWP_IN_CART_EXCLUSION_MIN_QTY
    );
  }

  /**
   * @param {number | string} variantId
   * @param {object | null} [config]
   * @returns {number | null}
   */
  function getGiftProductIdForVariant(variantId, config) {
    config = config || getConfig();
    if (!config?.gifts?.length) return null;

    const id = Number(variantId);
    if (!Number.isFinite(id)) return null;

    for (let i = 0; i < config.gifts.length; i++) {
      const gift = config.gifts[i];
      const variants = gift.variants || [];

      for (let j = 0; j < variants.length; j++) {
        if (Number(variants[j].id) === id) {
          return Number(gift.id);
        }
      }
    }

    return null;
  }

  /**
   * @param {{ items?: object[] }} cart
   * @param {object | null} [config]
   * @returns {boolean}
   */
  function hasAvailableGiftOptions(cart, config) {
    config = config || getConfig();
    if (!config?.gifts?.length) return false;

    return config.gifts.some(function (gift) {
      return !isProductInCartAsNonGift(cart, gift.id);
    });
  }

  window.GWP = {
    getConfig: getConfig,
    getLineProperty: getLineProperty,
    isGwpGiftItem: isGwpGiftItem,
    getSaleTag: getSaleTag,
    isSaleItem: isSaleItem,
    ensureProductTagData: ensureProductTagData,
    hasSaleItemInCart: hasSaleItemInCart,
    getEligibleSubtotal: getEligibleSubtotal,
    hasGiftInCart: hasGiftInCart,
    getGiftLineItem: getGiftLineItem,
    getGiftLineItems: getGiftLineItems,
    getGiftQuantity: getGiftQuantity,
    isValidGiftVariant: isValidGiftVariant,
    isGiftVariantAvailable: isGiftVariantAvailable,
    getCurrencyRate: getCurrencyRate,
    getThresholdCents: getThresholdCents,
    isEligibleForGift: isEligibleForGift,
    isProductInCartAsNonGift: isProductInCartAsNonGift,
    getNonGiftProductQuantity: getNonGiftProductQuantity,
    getGiftProductIdForVariant: getGiftProductIdForVariant,
    hasAvailableGiftOptions: hasAvailableGiftOptions,
  };
})();
